import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getRosterItems } from '../../../axios/RosterItem/RosterItemApi';
import { getShiftListAll } from '../../../axios/Shift/ShiftApi';
import { getUserList, getUserOfRoster } from '../../../axios/User/UserApi';
import { LoadingProvider, useLoading } from '../../../context/LoadingContext';
import Rosters from './Rosters';
import ShiftAndRosterView, { getRosterCycleDateRange } from './ShiftAndRosterView';
import Shifts from './Shifts';
import { addDays, format, parse, startOfDay } from 'date-fns';
import { getListGroup, getListUserGroup } from '../../../axios/Group/GroupUrlApi';
import { useRefresh } from '../../../context/RefreshContext';
import { CustomTabsV2 } from '../../../components/StyledComponents';
import { Tab, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ShiftRequest from './ShiftRequest';
import { useAuth } from '../../../context/AuthContext';
import { useSearchParams } from "react-router-dom";

const ShiftAndRoster = () => {
  const { refreshData, handleRefreshData } = useRefresh();
  const { t } = useTranslation();
  const [show, hide] = useLoading();
  const [searchParams] = useSearchParams();
  const requestShiftId = searchParams.get("requestShiftId");

  // Master-Detail states
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'detail'
  const [selectedRosterDetail, setSelectedRosterDetail] = useState(null);

  // Other states
  const [usersData, setUsersData] = useState([]);
  const [usersDataByGroup, setUsersDataByGroup] = useState([]);
  const [shiftsData, setShiftsData] = useState([]);
  const [rostersData, setRostersData] = useState([]);
  const [refreshDataAddRoster, setRefreshDataAddRoster] = useState(false);
  const [refreshDataAddUserRoster, setRefreshDataAddUserRoster] = useState(false);
  const [activeWeekTabIndex, setActiveWeekTabIndex] = useState(null);
  const [currentRoster, setCurrentRoster] = useState({});
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const [GroupData, setGroupData] = useState([]);
  const [allUser, setAllUser] = useState([]);
  const { isEmployee, profile, weekStartDay } = useAuth();

  const [loadingRoster, setLoadingRoster] = useState(false);

  const [selectedShiftDateTime, setSelectedShiftDateTime] = useState({
    startDate: startOfDay(new Date()),
    endDate: startOfDay(new Date()),
  });

  const [tabValue, setTabValue] = useState(() =>
    searchParams.get("requestShiftId") ? 2 : 0
  );

  // Reset mọi thứ khi component mount (reload trang)
  useEffect(() => {
    setViewMode('list');
    setSelectedRosterDetail(null);
    setRostersData([]);
    setUsersData([]);
    setCurrentRoster({});
    setSelectedShiftId("");
    setActiveWeekTabIndex(null);
  }, []);

  const handleChangeTab = useCallback((event, newValue) => {
    setTabValue(newValue);

    // Luôn reset khi chuyển tab để tránh giữ state cũ
    setViewMode('list');
    setSelectedRosterDetail(null);
    setRostersData([]);
    setUsersData([]);
    setCurrentRoster({});
    setSelectedShiftId("");
    setActiveWeekTabIndex(null);
  }, []);

  // Tour: chuyển về tab Rosters (list) / mở detail giả
  useEffect(() => {
    const todayStr = format(startOfDay(new Date()), "yyyy-MM-dd");

    const TOUR_FAKE_ROSTER_DETAIL = {
      rosterId: "tour-fake-roster",
      rosterName: "Sample Roster",
      repetition: 1,
      startDate: todayStr,
      endBy: null,
      formatStartDate: format(startOfDay(new Date()), "dd/MM/yyyy"),
      formatEndBy: "",
      range: 7,
    };

    const TOUR_FAKE_USER = {
      userId: "tour-fake-user",
      fullName: "Sample User",
    };

    const TOUR_FAKE_ROSTER_ITEM = {
      rosterItemId: "tour-fake-item",
      userId: "tour-fake-user",
      shiftId: "tour-fake-shift",
      shiftName: "Morning",
      shiftTime: "08:00 - 17:00",
      weekDate: todayStr,
      day: "Monday",
      isSwapShift: false,
      isEnabled: true,
    };

    window.__setShiftAndRosterTab = (index) => {
      flushSync(() => {
        setViewMode("list");
        setSelectedRosterDetail(null);
        setTabValue(index);
      });
    };

    window.__openTourRosterDetail = () => {
      flushSync(() => {
        window.__closeAddRosterDialog?.();
        window.__closeTourEditRosterDialog?.();
        window.__closeTourDeleteRosterDialog?.();
        setTabValue(0);
        setSelectedRosterDetail(TOUR_FAKE_ROSTER_DETAIL);
        setViewMode("detail");
        setActiveWeekTabIndex(0);
        setSelectedShiftDateTime({
          startDate: startOfDay(new Date()),
          endDate: addDays(startOfDay(new Date()), 6),
        });
        // Fake user/item nếu chưa có dữ liệu thật
        setUsersData((prev) =>
          prev?.length > 0
            ? prev
            : [
                TOUR_FAKE_USER,
                { userId: "tour-fake-user-2", fullName: "Sample User 2" },
              ]
        );
        setRostersData((prev) =>
          prev?.length > 0 ? prev : [TOUR_FAKE_ROSTER_ITEM]
        );
        setShiftsData((prev) =>
          prev?.length > 0
            ? prev
            : [
                {
                  shiftId: "tour-fake-shift",
                  shiftName: "Morning",
                  shiftTime: "08:00 - 17:00",
                  shiftStart: "08:00",
                  shiftEnd: "17:00",
                },
              ]
        );
      });
    };

    window.__closeTourRosterDetail = () => {
      flushSync(() => {
        setViewMode("list");
        setSelectedRosterDetail(null);
      });
    };

    return () => {
      delete window.__setShiftAndRosterTab;
      delete window.__openTourRosterDetail;
      delete window.__closeTourRosterDetail;
    };
  }, []);

  const handleWeekTabIndexClick = useCallback((index) => {
    setActiveWeekTabIndex(index);
  }, []);

  const handleRefreshDataAddRoster = useCallback(() => {
    setRefreshDataAddRoster((prev) => !prev);
  }, []);

  const refreshRosterItemsOnly = useCallback(() => {
    setRefreshDataAddUserRoster((prev) => !prev);
  }, []);

  const handleRosterRowSelection = useCallback((newSelection) => {
    if (!newSelection?.rosterId) return;

    setSelectedRosterDetail(newSelection);
    setViewMode('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewMode('list');
    setSelectedRosterDetail(null);           // ← Fix chính: xóa selection để Rosters không tự re-select
    setRostersData([]);
    setUsersData([]);
    setCurrentRoster({});
    setSelectedShiftId("");
    setActiveWeekTabIndex(null);
  }, []);

  // Fetch all users
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const payload = {
          statusId: 1,
          page: 1,
          pageSize: 500,
          langId: localStorage.getItem("i18nextLngId"),
        };
        const response = await getUserList(payload);
        setAllUser(response.data.data || []);
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          toast.error(t("Có lỗi khi tải danh sách người dùng"));
        }
      }
    };
    fetchUser();
  }, [refreshData, t]);

  // Fetch groups
  useEffect(() => {
    const fetchListGroup = async () => {
      try {
        const payload = {
          pageNo: 1,
          recordPerPage: 50000,
          langId: localStorage.getItem("i18nextLngId"),
        };
        const response = await getListGroup(payload);
        setGroupData(response.data.data || []);
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          toast.error(t("Có lỗi khi tải danh sách nhóm"));
        }
      }
    };
    fetchListGroup();
  }, [refreshData, t]);

  const groupIds = useMemo(() => {
    return [
      ...new Set(
        rostersData
          .map(item => item.groupId)
          .filter(id => id !== null && id !== undefined)
      )
    ];
  }, [rostersData]);

  useEffect(() => {
    if (!groupIds.length) return;

    const fetchUsersByGroups = async () => {
      show();
      try {
        const responses = await Promise.all(
          groupIds.map(groupId =>
            getListUserGroup({
              group: groupId,
              pageNo: 1,
              recordPerPage: 10000,
              langId: localStorage.getItem("i18nextLngId"),
            })
          )
        );

        // Gom tất cả user lại thành 1 mảng
        const mergedUsers = responses.flatMap((res, index) => {
          const groupId = groupIds[index];

          return (res.data.data || []).map(user => ({
            ...user,
            groupId // 👈 thêm cái này
          }));
        });

        const uniqueUsers = Array.from(
          new Map(mergedUsers.map(u => [u.id, u])).values()
        );

        setUsersDataByGroup(mergedUsers);

      } catch (error) {
        if (error.response?.status !== 401) {
          toast.error(t(error?.response?.data.message) || t('common.network-timeout'));
        }
      } finally {
        hide();
      }
    };

    fetchUsersByGroups();
  }, [groupIds]);

  // Update default date range when roster selected (only in detail)
  useEffect(() => {
    if (viewMode !== 'detail' || !selectedRosterDetail?.repetition || selectedRosterDetail.repetition <= 0) return;

    const { startDate, endDate, weekIndex } = getRosterCycleDateRange(
      startOfDay(new Date()),
      selectedRosterDetail.startDate,
      selectedRosterDetail.repetition,
      weekStartDay
    );

    setSelectedShiftDateTime({ startDate, endDate });
    setActiveWeekTabIndex(weekIndex);
  }, [viewMode, selectedRosterDetail, weekStartDay]);

  // Fetch roster items
  const fetchRosterItem = useCallback(async (payload) => {
    try {
      setLoadingRoster(true);
      const { data } = await getRosterItems(payload);
      setRostersData(data.data ?? []);
    } catch (error) {
      if (!isAxiosError(error) || error.response?.status !== 401) {
        toast.error(t("Có lỗi khi tải chi tiết roster"));
      }
    } finally {
      setLoadingRoster(false);
    }
  }, [t, refreshData]);

  const isTourFakeRoster =
    selectedRosterDetail?.rosterId === "tour-fake-roster";

  useEffect(() => {
    if (tabValue !== 0 || viewMode !== 'detail' || !selectedRosterDetail?.rosterId) {
      if (viewMode !== 'detail') setRostersData([]);
      return;
    }

    // Tour fake roster — giữ data giả, không gọi API
    if (isTourFakeRoster) {
      setLoadingRoster(false);
      return;
    }

    let fromDate, endDate;

    if (Object.keys(currentRoster).length === 0) {
      fromDate = format(selectedShiftDateTime.startDate, 'yyyy-MM-dd');
      endDate = format(selectedShiftDateTime.endDate, 'yyyy-MM-dd');
    } else {
      try {
        fromDate = format(parse(currentRoster.formatStartDate, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
        endDate = format(parse(currentRoster.formatEndBy || currentRoster.formatStartDate, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
      } catch (err) {
        console.error("[fetchRosterItem] Lỗi parse ngày:", err);
        return;
      }
    }

    const payload = {
      ...selectedRosterDetail,
      fromDate,
      endDate,
      shiftId: selectedShiftId,
      langId: localStorage.getItem("i18nextLngId"),
    };

    fetchRosterItem(payload);
  }, [
    tabValue,
    viewMode,
    selectedRosterDetail,
    selectedShiftDateTime.startDate,
    selectedShiftDateTime.endDate,
    selectedShiftId,
    currentRoster.formatStartDate,
    currentRoster.formatEndBy,
    refreshDataAddUserRoster,
    fetchRosterItem,
    isTourFakeRoster,
  ]);

  // Fetch users of roster
  useEffect(() => {
    if (viewMode !== 'detail' || !selectedRosterDetail?.rosterId) return;

    // Tour fake roster — giữ user giả, không gọi API
    if (isTourFakeRoster) return;

    const fetchUserOfRoster = async () => {
      try {
        const payload = {
          rosterId: selectedRosterDetail.rosterId,
          langId: localStorage.getItem("i18nextLngId"),
        };
        const response = await getUserOfRoster(payload);
        setUsersData(response.data.data || []);
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          toast.error(t("Có lỗi khi tải người dùng của roster"));
        }
      }
    };

    fetchUserOfRoster();
  }, [viewMode, selectedRosterDetail?.rosterId, refreshDataAddUserRoster, t, isTourFakeRoster]);

  // Fetch all shifts
  useEffect(() => {
    const fetchShift = async () => {
      try {
        const response = await getShiftListAll(localStorage.getItem("i18nextLngId"));
        setShiftsData(response.data.listShift || []);
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          toast.error(t("Có lỗi khi tải danh sách ca"));
        }
      }
    };
    fetchShift();
  }, [refreshData, t]);

  const COLORS = [
    '#93C5FD', '#FCA5A5', '#6EE7B7', '#FB7185', '#7DD3FC',
    '#FCD34D', '#5EEAD4', '#F0ABFC', '#86EFAC', '#60A5FA',
    '#FDBA74', '#67E8F9', '#C4B5FD', '#D9F99D', '#A5B4FC',
    '#FEF08A', '#FBCFE8', '#94A3B8', '#A1A1AA', '#CBD5E1',
  ];

  const shiftColorMap = useMemo(() => {
    const map = {};
    shiftsData.forEach((shift, i) => {
      map[shift.shiftId] = COLORS[i % COLORS.length];
    });
    return map;
  }, [shiftsData]);

  const finalRosters = useMemo(() => {
    if (!isEmployee) return rostersData;

    return rostersData.filter((r) => {
      const isPersonal =
        Number(r.userId) === Number(profile.userId);

      const isGroupRoster =
        r.groupId != null &&
        usersDataByGroup.some(
          user =>
            Number(user.id) === Number(profile.userId) &&
            Number(user.groupId) === Number(r.groupId)
        );

      return isPersonal || isGroupRoster;
    });

  }, [isEmployee, rostersData, usersDataByGroup, profile.userId]);

  useEffect(() => {
    if (requestShiftId) {
      setTabValue(2);
    }
  }, [requestShiftId]);

  return (
    <div className="shift-and-roster">
      <div className="bg-white rounded-[10px] shadow-[0px_2px_10px_rgba(0,0,0,0.1)] overflow-hidden">

        <CustomTabsV2
          variant="fullWidth"
          indicatorColor="#FFFFFF"
          value={tabValue}
          onChange={handleChangeTab}
        >
          <Tab
            label={t('common.rosters')}
            {...a11yProps(0)}
            disableRipple
            data-tour="shift-and-roster-roster"
          />
          <Tab
            label={t('common.shifts')}
            {...a11yProps(1)}
            disableRipple
            data-tour="shift-and-roster-shift"
          />
          <Tab
            label={t('common.content-170')}
            {...a11yProps(2)}
            disableRipple
            data-tour="shift-and-roster-request"
          />
        </CustomTabsV2>

        <CustomTabPanel value={tabValue} index={0}>
          {viewMode === 'list' ? (
            <div className="px-3 pb-3 w-full min-w-0">
              <LoadingProvider>
                <Rosters
                  onRowSelection={handleRosterRowSelection}
                  refreshData={refreshData}
                  onRefresh={handleRefreshData}
                  handleActiveWeekTabIndex={handleWeekTabIndexClick}
                  allUser={allUser}
                  onChangeAllUser={setAllUser}
                  GroupData={GroupData}
                  handleRefreshDataAddRoster={handleRefreshDataAddRoster}
                  refreshDataAddRoster={refreshDataAddRoster}
                  selectedRows={selectedRosterDetail || {}}
                  usersData={usersData}
                  disableAutoSelect={viewMode === 'list'}
                />
              </LoadingProvider>
            </div>
          ) : (
            <div
              className="px-3 pb-3 w-full min-w-0 overflow-hidden"
              data-tour="shift-and-roster-detail"
            >
              <div className="flex items-center gap-4 my-2">
                <div
                  onClick={handleBackToList}
                  className="cursor-pointer text-[#1F2937] hover:text-black"
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </div>

                <h2 className="text-xl font-semibold truncate">
                  {selectedRosterDetail?.rosterName || t('common.roster_detail')}
                </h2>
              </div>

              <LoadingProvider>
                <div className="w-full overflow-x-auto">
                  <ShiftAndRosterView
                    rosters={finalRosters}
                    selectedRows={selectedRosterDetail}
                    shiftsData={shiftsData}
                    usersData={usersData}
                    activeWeekTabIndex={activeWeekTabIndex}
                    handleActiveWeekTabIndex={handleWeekTabIndexClick}
                    onRefresh={refreshRosterItemsOnly}
                    selectedShiftDateTime={selectedShiftDateTime}
                    setSelectedShiftDateTime={setSelectedShiftDateTime}
                    updateRoster={setCurrentRoster}
                    getShiftSelected={(data) =>
                      setSelectedShiftId(data.selectedShift)
                    }
                    allUser={allUser}
                    onChangeAllUser={setAllUser}
                    GroupData={GroupData}
                    refreshData={refreshData}
                    shiftColorMap={shiftColorMap}
                    usersDataByGroup={usersDataByGroup}
                    handleRefreshData={handleRefreshData}
                    loadingRoster={loadingRoster}
                  />
                </div>
              </LoadingProvider>
            </div>
          )}
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={1}>
          <div className="px-3 pb-3 w-full min-w-0">
            <LoadingProvider>
              <div className="w-full overflow-x-auto">
                <Shifts
                  refreshData={refreshData}
                  onRefresh={handleRefreshData}
                />
              </div>
            </LoadingProvider>
          </div>
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={2}>
          <div className="px-3 sm:px-3 pb-3 w-full min-w-0">
            <ShiftRequest
              GroupData={GroupData}
              shiftsData={shiftsData}
              usersData={usersData}
              allUser={allUser}
              handleRefreshData={handleRefreshData}
              refreshData={refreshData}
            />
          </div>
        </CustomTabPanel>

      </div>
    </div>
  );

};

const CustomTabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && <div className='mt-3'>{children}</div>}
  </div>
);

const a11yProps = (index) => ({
  id: `user-tab-${index}`,
  'aria-controls': `user-tabpanel-${index}`,
});

export default ShiftAndRoster;